/**
 * Phase 24.4a — generic Modal primitive.
 *
 * Composable shell. Backdrop click + Esc key close behavior. Body
 * scrolling locked while open (prevents background scroll on mobile).
 * Pattern mirrors existing PmcExplainerModal/WeeklyCheckinModal but
 * extracted into reusable primitive.
 *
 * Accessibility: role="dialog", aria-modal="true". Minimal focus
 * management v1 (no focus trap library); harden if regression surfaces.
 *
 * Usage:
 *   <Modal isOpen={open} onClose={() => setOpen(false)} title="...">
 *     {children}
 *   </Modal>
 */
import { useEffect, type ReactNode } from "react";
import { X } from "lucide-react";
import FocusTrap from "focus-trap-react";
import { cn } from "@/lib/utils";

interface Props {
  isOpen: boolean;
  onClose: () => void;
  title?: string;
  children: ReactNode;
  /** Override max-width. Defaults to max-w-2xl. */
  maxWidth?: string;
  /** Show the close X button in header. Default true. */
  showCloseButton?: boolean;
}

export function Modal({
  isOpen,
  onClose,
  title,
  children,
  maxWidth = "max-w-2xl",
  showCloseButton = true,
}: Props) {
  useEffect(() => {
    if (!isOpen) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose();
    };
    document.addEventListener("keydown", onKey);
    // Lock body scroll while modal open (mobile: avoids background scroll)
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.removeEventListener("keydown", onKey);
      document.body.style.overflow = prevOverflow;
    };
  }, [isOpen, onClose]);

  if (!isOpen) return null;

  return (
    <FocusTrap
      active={isOpen}
      focusTrapOptions={{
        // We handle Esc + backdrop click ourselves at parent level —
        // suppress focus-trap's default Esc/click-outside deactivation
        // so it doesn't fight onClose.
        escapeDeactivates: false,
        clickOutsideDeactivates: false,
        // Return focus to triggering element on unmount.
        returnFocusOnDeactivate: true,
        // Allow trap to mount even if no initially focusable child
        // (defensive — modal with only text content + close-X button).
        fallbackFocus: '[data-testid="modal-content"]',
      }}
    >
      <div
        className="fixed inset-0 z-50 flex items-center justify-center bg-navy-deep/60 p-4"
        onClick={onClose}
        role="dialog"
        aria-modal="true"
        aria-label={title}
        data-testid="modal-backdrop"
      >
        <div
          onClick={(e) => e.stopPropagation()}
          tabIndex={-1}
          className={cn(
            "flex max-h-[90vh] w-full flex-col rounded-2xl border border-cream-border bg-cream-light shadow-soft",
            maxWidth,
          )}
          data-testid="modal-content"
        >
          {(title || showCloseButton) && (
            <div className="flex items-start justify-between gap-3 border-b border-cream-border p-5">
              {title && (
                <h2 className="font-serif text-[20px] font-medium leading-snug text-navy-deep">
                  {title}
                </h2>
              )}
              {showCloseButton && (
                <button
                  type="button"
                  onClick={onClose}
                  aria-label="Close"
                  className="ml-auto shrink-0 rounded-full p-1.5 text-taupe hover:bg-cream-border hover:text-navy-deep"
                  data-testid="modal-close"
                >
                  <X className="h-4 w-4" strokeWidth={2} />
                </button>
              )}
            </div>
          )}
          <div className="flex-1 overflow-y-auto p-5">{children}</div>
        </div>
      </div>
    </FocusTrap>
  );
}
